import React, { useState } from 'react';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';

import { updateStateAttribute } from '../helpers/helper';
import { useAppSelector } from '../hooks/hooks';
import './CLocalizedText.css';
import CModal from './CModal';

interface Props {
  dictKey: string;
  inline?: boolean;
  collapsible?: boolean;
  expandInModal?: boolean;
  wordLength?: number;
  onClickReadMore?: () => void;
}

interface State {
  isExpanded: boolean;
  isModal: boolean;
}

/**
 * To return as string, use <CLocalizedText .../> as unknown as string.
 * @param props
 * @constructor
 */
export default function CLocalizedText(props: Props) {
  const defaultWordLength = 50;
  const { localeDict } = useAppSelector((state) => state.dashboard);
  const [state, setState] = useState<State>({ isExpanded: false, isModal: false });

  const fullText = (
    <span className={'localized-text'}>
      <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex, rehypeRaw]}>
        {localeDict?.[props.dictKey] ?? `[${props.dictKey}]`}
      </Markdown>
      {props.collapsible && !props.expandInModal && (
        <>
          <div className={'h-2'} />
          <a
            onClick={() => {
              updateStateAttribute<State>({ isExpanded: false }, setState);
            }}
          >
            <Markdown>{localeDict?.globalReadLess}</Markdown>
          </a>
        </>
      )}
    </span>
  );

  const showEllipsis =
    props.collapsible &&
    (localeDict?.[props.dictKey] ? localeDict?.[props.dictKey].split(' ').length : 0) >
      (props.wordLength ?? defaultWordLength);
  const displayText = localeDict?.[props.dictKey]
    ? localeDict?.[props.dictKey]
        .split(' ')
        .slice(0, props.wordLength ?? defaultWordLength)
        .join(' ')
    : `[${props.dictKey}]`;
  return (
    <>
      {props.collapsible && !state.isExpanded ? (
        <span className={'inline-localized-text'}>
          <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex, rehypeRaw]}>
            {displayText}
          </Markdown>
          {(props.onClickReadMore ?? showEllipsis) ? (
            <>
              {showEllipsis && <span>...</span>}
              <a
                onClick={() => {
                  if (props.onClickReadMore) {
                    props.onClickReadMore();
                  } else {
                    updateStateAttribute<State>(
                      {
                        isExpanded: !props.expandInModal,
                        isModal: props.expandInModal
                      },
                      setState
                    );
                  }
                }}
                className={'inline-localized-text inline'}
              >
                <>
                  &nbsp;&nbsp;
                  <Markdown>{localeDict?.globalReadMore}</Markdown>
                </>
              </a>
            </>
          ) : (
            <></>
          )}
        </span>
      ) : (
        fullText
      )}
      <CModal
        onClose={() => {
          updateStateAttribute<State>({ isModal: false }, setState);
        }}
        open={state.isModal}
      >
        {fullText}
      </CModal>
    </>
  );
}
